import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * Tabs collection: every panel is fetched on first activation via the Section Rendering API
 * against the tab's collection URL and kept in the DOM afterwards. Panels are matched to tabs by
 * position; the collection URL lives on the tab button (`data-url`).
 *
 * @extends {Component<Refs>}
 */
class StTabsCollection extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    const { tabs = [], panels = [] } = this.refs;
    for (const [i, tab] of tabs.entries()) {
      const panel = panels[i];
      if (panel) {
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', tab.id);
      }
    }
    this.#select(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  get #tabs() {
    return this.refs.tabs ?? [];
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (tab) this.#select(this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab)));
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index === -1) return;

    const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
    const offsets = {
      ArrowLeft: -direction,
      ArrowRight: direction,
      Home: -index,
      End: tabs.length - 1 - index,
    };
    const offset = offsets[/** @type {keyof typeof offsets} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const next = (index + offset + tabs.length) % tabs.length;
    this.#select(next);
    tabs[next]?.focus();
  };

  /** Theme editor: selecting a tab block in the sidebar activates that tab. @param {Event} event */
  #handleBlockSelect = (event) => {
    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index !== -1) this.#select(index);
  };

  /** @param {number} index */
  #select(index) {
    const { tabs = [], panels = [] } = this.refs;
    if (index < 0) return;

    for (const [i, tab] of tabs.entries()) {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    const panel = panels[index];
    const url = tabs[index]?.dataset.url;
    if (panel && url) this.#load(panel, url);
  }

  /**
   * Fetches the panel grid once; the morphed DOM is the cache.
   * @param {HTMLElement} panel
   * @param {string} url - The tab's collection URL
   */
  async #load(panel, url) {
    const { loaded } = panel.dataset;
    const { sectionId } = this.dataset;
    if (!sectionId || loaded || panel.getAttribute('aria-busy') === 'true') return;

    panel.setAttribute('aria-busy', 'true');
    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, !Shopify.designMode, new URL(url, location.origin));
      const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('.st-tabs-collection__grid');
      const target = panel.querySelector('.st-tabs-collection__grid');
      if (!grid || !target) throw new Error('Panel grid missing in section render response');

      morph(target, grid);
      panel.dataset.loaded = 'true';
    } catch (error) {
      console.error('Tabs collection error:', error instanceof Error ? error.message : error);
    } finally {
      panel.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('st-tabs-collection')) {
  customElements.define('st-tabs-collection', StTabsCollection);
}
